import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DividerList } from 'components/_new/Divider';
import { Link } from 'components/_new/Link';
import { usePanel } from 'contexts/panelContext';
import { KnowledgeBaseArticleContentItem } from 'types/KnowledgeBase';
import { ArticleItem } from 'components/knowledgeBase/ArticleItem';
import { ArticlePresenterDialog } from 'components/knowledgeBase/ArticlePresenterDialog';
import { useParams } from 'react-router-dom';
import { ReadedIcon } from 'components/knowledgeBase/ReadedIcon';
import { ToreadIcon } from 'components/knowledgeBase/ToreadIcon';
import { FavoriteIcon } from 'components/knowledgeBase/FavoriteIcon';
import { useAuth } from 'contexts/authContext';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';

type MaterialsSubpageProps = {
	articles?: KnowledgeBaseArticleContentItem[];
	refetch?: () => void;
};

export const MaterialsSubpage = ({ articles, refetch }: MaterialsSubpageProps) => {
	const { t } = useTranslation();
	const { organizationName, programName, type: panelType } = useParams();
	const { panelLinkPrefix } = usePanel();
	const { knowledgeBaseLinkPrefix } = useKnowledgeBase();

	const [selectedArticle, setSelectedArticle] = useState<number | null>(null);

	const { currentUser } = useAuth();
	if (!articles || (articles && !articles.length)) {
		return <div />;
	}

	const visibleArticles = currentUser?.isProfessionalViewEnabled
		? articles
		: articles.filter((item) => !item.isProfessional);

	const list = visibleArticles.map((data) => {
		return (
			<ArticleItem
				key={data.articleId}
				data={data}
				linkPrefix={knowledgeBaseLinkPrefix}
				onPreviewClick={() => {
					setSelectedArticle(data.articleId);
				}}
				showButtonsOnHover={false}
				openPreviewOnClick
				slim
			/>
		);
	});

	const contentParserOptions = {
		handlers: {
			onArticleLinkClick: (articleId: number) => {
				setSelectedArticle(articleId);
			},
		},
		replaces: {
			panelPrefix: panelLinkPrefix,
			organization: organizationName,
			program: programName,
			usertype: panelType,
		},
		disableFormat: true,
	};

	const handleArticleChange = (newArticleId: number) => {
		setSelectedArticle(newArticleId);
	};

	return (
		<>
			<ArticlePresenterDialog
				articleId={selectedArticle}
				visible={Boolean(selectedArticle)}
				onHide={() => {
					setSelectedArticle(null);
				}}
				contentParserOptions={contentParserOptions}
				sessionArticles={articles}
				onArticleChange={handleArticleChange}
				onFlagChange={() => {
					refetch?.();
				}}
				knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
			/>
			<div className="flex flex-column gap-2">
				<h2 className="my-5">{t('userPanel.sessionsNew.materials.heading')}</h2>

				<p className="mt-2 mb-6">
					<Trans
						t={t}
						i18nKey="userPanel.sessionsNew.materials.info"
						components={[
							<Link to={`/${knowledgeBaseLinkPrefix}`} />,
							<Link to={`/${knowledgeBaseLinkPrefix}`} />,
						]}
					/>
				</p>

				<div className="flex flex-column gap-3 md:flex-1">
					{list.length === 0 ? (
						<span className="text-muted">{t('userPanel.sessionsNew.materials.noMaterials')}</span>
					) : (
						<DividerList className="md:flex-1" gap={0}>
							{list}
						</DividerList>
					)}
				</div>
			</div>
		</>
	);
};
